import { ReactNode, useState } from 'react'
import { Cancel, CheckCircle, HourglassTop, InfoOutlined } from '@mui/icons-material'
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  MenuItem,
  Skeleton,
  Switch,
  TextField,
  Typography,
} from '@mui/material'
import { CollegeOption } from '@/api/financial/application/college'
import {
  FinancialPerson,
  FinancialProposal,
} from '@/api/financial/application/read-application'
import { AllowancePreview, AllowanceLimits } from '@/api/financial/simulation/simulate-allowance'
import { MunicipalityOption } from '@/api/financial/person/municipalities'
import { CepSearchField } from '@/components/address-form/cep-search-field'
import { RouterLink } from '@/components/router-link'
import { routes } from '@/routes'
import { BANKS } from '@/pages/credit/banks'
import { GraduationPicker } from '@/pages/session/profile/graduation-picker'
import { graduationYearMin } from '@/pages/session/profile/profile-steps'
import {
  AmountStepper,
  ComboSwitch,
  Field,
  MoneyField,
  OptionCards,
  PillGroup,
} from '@/components/credit-journey/fields'
import { DocumentDropzone } from '@/components/document-input/document-dropzone'
import { MaskedInput } from '@/components/masked-input/masked-input'
import { states } from '@/constants/states'
import { CepResponse } from '@/types'
import { AllowanceStep, documentList, isMarried } from './allowance-flow'
import { AllowanceForm, digits } from './allowance-form'
import { ComboDetails } from './combo-details'

export const SCHOOL = [
  'Sem instrução escolar',
  'Ensino fundamental incompleto',
  'Ensino fundamental completo',
  'Ensino médio incompleto',
  'Ensino médio completo',
  'Graduação incompleta',
  'Graduação completa',
  'Pós-graduação incompleta',
  'Pós-graduação completa',
  'Mestrado incompleto',
  'Mestrado completo',
  'Doutorado incompleto',
  'Doutorado completo',
]

export const MARITAL = [
  { value: 'single', label: 'Solteiro' },
  { value: 'married', label: 'Casado' },
  { value: 'civil_union', label: 'União estável' },
  { value: 'divorced', label: 'Divorciado' },
  { value: 'widowed', label: 'Viúvo' },
  { value: 'separated', label: 'Separado' },
]

const PURPOSES = [
  { value: 'daily_expenses', label: 'Gastos diários' },
  { value: 'residency_prep_course', label: 'Cursinho para residência' },
  { value: 'college_tuition', label: 'Mensalidades da faculdade' },
  { value: 'medical_supplies', label: 'Materiais e equipamentos médicos' },
  { value: 'pay_debts', label: 'Pagar dívidas ou empréstimos' },
  { value: 'other', label: 'Outro' },
]

const SCHOLARSHIPS = [
  { value: 'prouni', label: 'PROUNI' },
  { value: 'college', label: 'Bolsa da faculdade' },
  { value: 'other', label: 'Outra' },
]

const FINANCING = [
  { value: 'fies', label: 'FIES' },
  { value: 'college', label: 'Da faculdade' },
  { value: 'banks', label: 'Em bancos' },
  { value: 'alume', label: 'Financiamento Alume' },
  { value: 'other', label: 'Outro' },
]

export const PAYMENT_SOURCES = [
  { value: 'own_income', label: 'Renda própria' },
  { value: 'family_income', label: 'Renda familiar' },
  { value: 'asset_sale', label: 'Venda de bens' },
  { value: 'loans', label: 'Empréstimos' },
  { value: 'financing', label: 'Financiamentos' },
  { value: 'family_help', label: 'Ajuda de familiares' },
  { value: 'friends_help', label: 'Ajuda de amigos' },
]

export const HIGH_SCHOOL = [
  { value: 'all_public', label: 'Todo em escola pública' },
  { value: 'all_private', label: 'Todo em escola privada' },
  { value: 'all_abroad', label: 'Todo no exterior' },
  { value: 'mostly_public', label: 'Maior parte em escola pública' },
  { value: 'mostly_private', label: 'Maior parte em escola privada' },
  { value: 'brazil_and_abroad', label: 'Parte no Brasil, parte no exterior' },
]

export const PLANS = [
  { value: 'armed_forces', label: 'Forças Armadas' },
  { value: 'emergency_shifts', label: 'Plantões em pronto-socorro' },
  { value: 'medical_residency', label: 'Residência médica' },
  { value: 'post_graduation', label: 'Pós-graduação' },
  { value: 'primary_care', label: 'Atuar em UBS' },
  { value: 'other', label: 'Outro' },
]

export const RESIDENCY = [
  { value: 'not_planned', label: 'Não pretendo' },
  { value: 'right_after_graduation', label: 'Logo após formado' },
  { value: 'six_months_to_one_year', label: 'Entre 6 meses e 1 ano' },
  { value: 'one_to_two_years', label: 'Entre 1 e 2 anos' },
  { value: 'after_two_years', label: 'Após 2 anos' },
]

export const YES_NO = [
  { value: 'yes', label: 'Sim' },
  { value: 'no', label: 'Não' },
]

export const PEP_INFO = (
  <>
    <b>Pessoa Politicamente Exposta (PPE)</b>
    <span>
      É considerada uma Pessoa Politicamente Exposta (PPE), aquela que desempenha ou desempenhou
      nos últimos cinco anos anteriores, no Brasil ou em países, territórios e dependências
      estrangeiros, cargos, empregos ou funções públicas relevantes.
    </span>
  </>
)

export const US_PERSON_INFO = (
  <>
    <b>São considerados US Person:</b>
    <span>
      Detentores de Cidadania Norte Americana, incluindo os detentores de dupla nacionalidade e
      passaporte norte-americano, ainda que residam fora dos Estados Unidos;
    </span>
    <span>
      Residente Fiscal nos EUA, ou seja, possua Green-Card e que possa se tornar um residente
      permanente legal;
    </span>
    <span>
      Presença física substancial nos Estados Unidos para fins tributários por pelo menos: 31
      dias durante o ano corrente; 183 dias nos últimos três anos;
    </span>
  </>
)

type CivilValues = {
  marital: string
  spouse_name: string
  spouse_tax_id: string
  politically_exposed: string
  us_person: string
}

/** Estado civil, cônjuge e as declarações PPE e US Person, iguais nos três produtos. */
export function CivilFields({
  form,
  patch,
}: {
  form: CivilValues
  patch: (partial: Partial<CivilValues>) => void
}) {
  return (
    <Box sx={{ display: 'grid', gap: 2 }}>
      <TextField
        select
        label="Estado civil"
        value={form.marital}
        onChange={(event) => patch({ marital: event.target.value })}
      >
        {MARITAL.map((item) => (
          <MenuItem key={item.value} value={item.value}>
            {item.label}
          </MenuItem>
        ))}
      </TextField>
      {isMarried(form.marital) && (
        <>
          <TextField
            label="Nome completo do cônjuge"
            value={form.spouse_name}
            onChange={(event) => patch({ spouse_name: event.target.value })}
          />
          <Field label="CPF do cônjuge">
            <MaskedInput
              mask="000.000.000-00"
              value={form.spouse_tax_id}
              onChange={(event) => patch({ spouse_tax_id: event.target.value })}
            />
          </Field>
        </>
      )}
      <PillGroup
        label="É politicamente exposto?"
        info={PEP_INFO}
        value={form.politically_exposed}
        onChange={(value) => patch({ politically_exposed: String(value) })}
        options={YES_NO}
      />
      <PillGroup
        label="É US Person?"
        info={US_PERSON_INFO}
        value={form.us_person}
        onChange={(value) => patch({ us_person: String(value) })}
        options={YES_NO}
      />
    </Box>
  )
}

const DOCUMENTS: Record<string, { label: string; description: string; optional?: boolean }> = {
  personal: {
    label: 'Documento de identificação com foto',
    description:
      'O documento anexado deve constar o seu CPF e a sua foto. Não esqueça de anexar frente e verso.',
  },
  proof_of_registration: {
    label: 'Comprovante de matrícula e/ou Histórico Escolar',
    description:
      'Não são válidos printscreens do site da Instituição de Ensino, documentos emitidos há mais de 2 meses, boletos ou contratos de prestação de serviços da faculdade. Dê preferência para arquivos em PDF.',
  },
  address: {
    label: 'Comprovante de endereço',
    description:
      'São considerados comprovantes de residência válidos: contas de consumo (água, luz, telefone, etc.), boletos ou faturas com valor e vencimento visíveis.',
    optional: true,
  },
  proof_of_scholarship: {
    label: 'Comprovante de bolsa de estudos',
    description: '',
    optional: true,
  },
}

export function parseGraduation(value: string) {
  const match = /^(\d{4})-(\d{2})$/.exec(value)
  if (!match) return null
  return { year: Number(match[1]), month: Number(match[2]) - 1 }
}

function graduationMonth(year: number, month: number | null) {
  if (month == null) return ''
  return `${year}-${String(month + 1).padStart(2, '0')}`
}

export function AllowanceGraduation({
  value,
  onChange,
}: {
  value: string
  onChange: (value: string) => void
}) {
  const parsed = parseGraduation(value)
  const [year, setYear] = useState(parsed?.year ?? graduationYearMin + 1)

  return (
    <GraduationPicker
      year={parsed?.year ?? year}
      month={parsed?.month ?? null}
      onChange={(nextYear, nextMonth) => {
        setYear(nextYear)
        onChange(graduationMonth(nextYear, nextMonth))
      }}
    />
  )
}

export function reais(value: number) {
  return value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
}

export function pct(rate: number) {
  return `${(rate * 100).toFixed(2).replace('.', ',')}%`
}

export function brazilDate(iso: string) {
  return new Intl.DateTimeFormat('pt-BR', { timeZone: 'America/Sao_Paulo' }).format(
    new Date(iso),
  )
}

export function Locked({ label, value }: { label: string; value: string }) {
  return (
    <Field label={label}>
      <Typography sx={{ bgcolor: 'action.hover', borderRadius: 1.5, px: 1.75, py: 1.5 }}>
        {value || '—'}
      </Typography>
    </Field>
  )
}

export function AllowanceScreens({
  step,
  form,
  patch,
  colleges,
  onCollegeSearch,
  semester,
  minSemester,
  limits,
  preview,
  identity,
  municipalities,
  addressState,
  onStateChange,
  onCep,
  documents,
  onUpload,
  onRemove,
  uploadingType,
  phoneVerified,
  denied,
  proposal,
  proposalExpired,
  reissuing,
  onReissue,
  previewLoading = false,
}: {
  step: AllowanceStep
  form: AllowanceForm
  patch: (partial: Partial<AllowanceForm>) => void
  colleges: CollegeOption[]
  onCollegeSearch: (value: string) => void
  semester: number
  minSemester: number
  limits: Pick<AllowanceLimits, 'min' | 'max' | 'step'> | null
  preview: AllowancePreview | undefined
  previewLoading?: boolean
  identity: { name: string; taxId: string; email: string; phone: string }
  municipalities: MunicipalityOption[]
  addressState: string
  onStateChange: (stateCode: string) => void
  onCep: (cep: CepResponse) => void
  documents: FinancialPerson['documents']
  onUpload: (type: string, file: File) => void
  onRemove: (documentKey: string) => void | Promise<unknown>
  uploadingType?: string
  phoneVerified: boolean
  denied: boolean
  proposal: FinancialProposal | null
  proposalExpired: boolean
  reissuing: boolean
  onReissue: () => void
}) {
  const [detailsOpen, setDetailsOpen] = useState(false)
  const phase = preview?.summary.phases[0]
  const accounting = preview?.limits?.accounting_price ?? 229.5
  const installment = (phase?.value ?? 0) + (form.combo ? accounting : 0)
  const rate = form.combo ? preview?.limits?.rates.combo : preview?.limits?.rates.solo
  const months = phase?.installments ?? 0
  const graceMonths = months === 23 ? '1 mês' : '2 meses'

  if (step === 'fac') {
    const college = colleges.find((item) => item.key === form.collegeKey) ?? null
    return (
      <Box sx={{ display: 'grid', gap: 2 }}>
        <Autocomplete
          options={colleges}
          value={college}
          getOptionLabel={(option) => option.name}
          isOptionEqualToValue={(option, value) => option.key === value.key}
          onInputChange={(_, value) => onCollegeSearch(value)}
          onChange={(_, option) =>
            patch({
              collegeKey: option?.key ?? '',
              collegePrivate: option?.college_type === 'private',
            })
          }
          renderInput={(params) => <TextField {...params} label="Instituição de ensino" />}
        />
        {form.collegePrivate && (
          <MoneyField
            label="Valor da mensalidade"
            value={form.tuition}
            onChange={(value) => patch({ tuition: value })}
            help="Entre R$ 3.000 e R$ 18.000."
          />
        )}
        <Field
          label="Previsão de formatura"
          help={
            semester >= minSemester
              ? `Você está no ${semester}º semestre.`
              : `A Mesada começa no ${minSemester}º semestre. Pela data, você está no ${Math.max(semester, 1)}º.`
          }
        >
          <AllowanceGraduation
            value={form.graduation}
            onChange={(graduation) => patch({ graduation })}
          />
        </Field>
      </Box>
    )
  }

  if (step === 'valor') {
    if (!limits) {
      return (
        <Box sx={{ display: 'grid', gap: 2, maxWidth: 460, mx: 'auto', width: '100%' }}>
          <Skeleton width={160} sx={{ fontSize: 40, mx: 'auto' }} />
          <Skeleton variant="rounded" height={40} />
        </Box>
      )
    }
    const amount = Math.min(limits.max, Math.max(limits.min, form.amount))
    return (
      <AmountStepper
        value={amount}
        min={limits.min}
        max={limits.max}
        step={limits.step}
        onChange={(next) => patch({ amount: next })}
      />
    )
  }

  if (step === 'plano') {
    const calculating = previewLoading || !preview
    const pending = (
      <Skeleton variant="text" width={72} sx={{ display: 'inline-block', fontSize: 'inherit' }} />
    )
    const rateText = calculating || !rate ? pending : pct(rate)
    const Mark = form.combo ? CheckCircle : Cancel
    const benefits: ReactNode[] = form.combo
      ? [
          <>
            <b>Juros reduzidos:</b>{' '}
            <Box component="b" sx={{ color: 'creditJourney.gain' }}>
              {rateText}
            </Box>{' '}
            / mês.
          </>,
          <>
            <b>Redução de impostos</b> nos plantões, com abertura de PJ médica.
          </>,
          <>
            <b>50% de desconto</b> na mensalidade contábil.
          </>,
        ]
      : [
          <>
            <b>Sem juros reduzidos:</b>{' '}
            <Box component="b" sx={{ color: 'creditJourney.loss' }}>
              {rateText}
            </Box>{' '}
            / mês.
          </>,
          <>
            Risco de <b>pagar até 3 vezes mais impostos</b> quando formado.
          </>,
          <>
            <b>Perca a única chance</b> de obter um desconto exclusivo.
          </>,
        ]
    const card = {
      border: 1,
      borderColor: 'divider',
      borderRadius: 1,
      px: 2.25,
      pt: 2.75,
      pb: 2.25,
    }
    const highlight = (text: string, computed = true) =>
      computed && calculating ? (
        pending
      ) : (
        <Box
          component="b"
          sx={{ color: 'primary.light', fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}
        >
          {text}
        </Box>
      )
    return (
      <Box sx={{ display: 'grid', gap: 2 }}>
        <Box sx={card}>
          <Typography sx={{ fontSize: 15, fontWeight: 500, mb: 0.75 }}>Durante o curso</Typography>
          <Typography sx={{ fontSize: 15.5, lineHeight: 1.5, color: 'text.primary' }}>
            Você vai receber {highlight(reais(form.amount), false)} / mês, durante{' '}
            {highlight(`${months} meses.`)}
          </Typography>
        </Box>
        <Box
          sx={{
            ...card,
            position: 'relative',
            boxShadow: (theme) =>
              form.combo ? `0 0 0 2px ${theme.palette.secondary.main}, ${theme.shadows[1]}` : 'none',
            borderColor: form.combo ? 'transparent' : 'divider',
          }}
        >
          {form.combo && (
            <Box
              component="span"
              sx={{
                position: 'absolute',
                top: 6,
                right: -8,
                transform: 'rotate(14deg)',
                bgcolor: 'creditJourney.ribbon',
                color: 'creditJourney.ribbonText',
                fontSize: 13,
                fontWeight: 700,
                px: 1.25,
                py: 0.5,
                borderRadius: 0.5,
                boxShadow: 1,
              }}
            >
              Mais escolhido
            </Box>
          )}
          <Typography sx={{ fontSize: 17, fontWeight: 500, mb: 1.75, pr: 11.25 }}>
            {form.combo ? 'Mesada + Contabilidade Médica' : 'Mesada sem benefícios'}
          </Typography>
          <Box
            component="ul"
            sx={{
              m: 0,
              mt: 0.75,
              p: 0,
              listStyle: 'none',
              display: 'grid',
              gap: 1.25,
              fontSize: 15,
              color: 'creditJourney.benefitText',
              '& b': { color: 'text.primary' },
            }}
          >
            {benefits.map((item, index) => (
              <Box component="li" key={index} sx={{ display: 'flex', gap: 0.75, alignItems: 'flex-start' }}>
                <Mark sx={{ fontSize: 22, color: form.combo ? 'success.main' : 'error.main' }} />
                <span>{item}</span>
              </Box>
            ))}
          </Box>
          <ComboSwitch
            ariaLabel="Contabilidade"
            value={form.combo}
            onChange={(combo) => patch({ combo })}
            yesLabel="Com contabilidade"
            noLabel="Sem contabilidade"
          />
          {form.combo && (
            <Button
              variant="text"
              size="small"
              onClick={() => setDetailsOpen(true)}
              sx={{ display: 'flex', mx: 'auto', mt: 1.5, textDecoration: 'underline' }}
            >
              Ver mais detalhes
            </Button>
          )}
          <ComboDetails
            open={detailsOpen}
            onClose={() => setDetailsOpen(false)}
            rate={rate ? pct(rate) : ''}
            installment={reais(phase?.value ?? 0)}
            accounting={reais(accounting)}
            total={reais((phase?.value ?? 0) + accounting)}
            months={months}
          />
        </Box>
        <Box sx={card}>
          <Typography sx={{ fontSize: 15, fontWeight: 500, mb: 0.75 }}>Após a formatura</Typography>
          <Typography sx={{ fontSize: 15.5, lineHeight: 1.5, color: 'text.primary' }}>
            Você vai pagar {highlight(reais(installment))} / mês, durante{' '}
            {highlight(`${months} meses.`)}
          </Typography>
          <FormControlLabel
            sx={{ mt: 1.25, mx: 0, alignItems: 'center', gap: 1.25, '& .MuiFormControlLabel-label': { fontSize: 14.5 } }}
            control={
              <Checkbox
                sx={{ p: 0 }}
                checked={form.grace}
                onChange={(event) => patch({ grace: event.target.checked })}
              />
            }
            label={`Adicionar carência de ${graceMonths}, após a formatura, para início dos pagamentos.`}
          />
        </Box>
        <Box
          component="ul"
          sx={{ m: 0, pl: 2.25, display: 'grid', gap: 0.5, fontSize: 13.5, color: 'text.secondary' }}
        >
          <li>
            Caso o pagamento seja realizado antecipadamente e/ou em menos parcelas o valor total a
            ser pago será menor.
          </li>
          <li>Os valores apresentados são estimativas e estão sujeitos à análise de crédito.</li>
        </Box>
      </Box>
    )
  }

  if (step === 'eu') {
    return (
      <Box sx={{ display: 'grid', gap: 2 }}>
        {!phoneVerified && (
          <Alert
            icon={false}
            severity="warning"
            action={
              <Button
                size="small"
                variant="text"
                color="warning"
                component={RouterLink}
                to={routes.account}
              >
                Verificar telefone
              </Button>
            }
          >
            Cadastre e verifique seu celular no perfil para continuar.
          </Alert>
        )}
        <Locked label="Nome completo" value={identity.name} />
        <Locked label="CPF" value={identity.taxId} />
        <Locked label="E-mail" value={identity.email} />
        <Locked label="Celular" value={identity.phone} />
        <Field label="Data de nascimento">
          <TextField
            type="date"
            value={form.birthdate}
            onChange={(event) => patch({ birthdate: event.target.value })}
            slotProps={{ inputLabel: { shrink: true } }}
          />
        </Field>
        <PillGroup
          label="Sexo"
          value={form.gender}
          onChange={(value) => patch({ gender: String(value) })}
          options={[
            { value: 'male', label: 'Masculino' },
            { value: 'female', label: 'Feminino' },
            { value: 'other', label: 'Outro' },
          ]}
        />
      </Box>
    )
  }

  if (step === 'end') {
    return (
      <Box sx={{ display: 'grid', gap: 2 }}>
        <CepSearchField
          label="CEP"
          value={form.zip_code}
          onChange={(event) => patch({ zip_code: event.target.value })}
          onFindCep={onCep}
        />
        <Locked label="Rua" value={form.street} />
        <Locked label="Bairro" value={form.district} />
        <TextField
          label="Número"
          value={form.number}
          onChange={(event) => patch({ number: event.target.value })}
        />
        <TextField
          label="Complemento"
          value={form.complement}
          onChange={(event) => patch({ complement: event.target.value })}
        />
        <TextField
          select
          label="Estado"
          value={addressState}
          onChange={(event) => onStateChange(event.target.value)}
        >
          {states.map((item) => (
            <MenuItem key={item.code} value={item.code}>
              {item.name}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          label="Cidade"
          value={form.municipality_id}
          onChange={(event) => patch({ municipality_id: event.target.value })}
        >
          {municipalities.map((item) => (
            <MenuItem key={item.id} value={String(item.id)}>
              {item.name}
            </MenuItem>
          ))}
        </TextField>
      </Box>
    )
  }

  if (step === 'civil') {
    return <CivilFields form={form} patch={patch} />
  }

  if (step === 'pais') {
    return (
      <Box sx={{ display: 'grid', gap: 2 }}>
        <Box sx={{ display: 'grid', gap: 0.5 }}>
          {!form.father_not_registered && (
            <TextField
              label="Nome do pai (ou responsável)"
              value={form.father_name}
              onChange={(event) => patch({ father_name: event.target.value })}
            />
          )}
          <FormControlLabel
            labelPlacement="start"
            sx={{ justifySelf: 'end', mr: 0, '& .MuiFormControlLabel-label': { fontSize: 14 } }}
            control={
              <Switch
                size="small"
                checked={form.father_not_registered}
                onChange={(event) => patch({ father_not_registered: event.target.checked })}
              />
            }
            label="Pai não registrado"
          />
        </Box>
        {!form.father_not_registered && (
          <>
            <Field label="CPF do pai">
              <MaskedInput
                mask="000.000.000-00"
                value={form.father_tax_id}
                onChange={(event) => patch({ father_tax_id: event.target.value })}
              />
            </Field>
            <TextField
              select
              label="Escolaridade do pai"
              value={form.father_education_level}
              onChange={(event) => patch({ father_education_level: event.target.value })}
            >
              {SCHOOL.map((item) => (
                <MenuItem key={item} value={item}>
                  {item}
                </MenuItem>
              ))}
            </TextField>
          </>
        )}
        <TextField
          label="Nome da mãe (ou responsável)"
          value={form.mother_name}
          onChange={(event) => patch({ mother_name: event.target.value })}
        />
        <Field label="CPF da mãe">
          <MaskedInput
            mask="000.000.000-00"
            value={form.mother_tax_id}
            onChange={(event) => patch({ mother_tax_id: event.target.value })}
          />
        </Field>
        <TextField
          select
          label="Escolaridade da mãe"
          value={form.mother_education_level}
          onChange={(event) => patch({ mother_education_level: event.target.value })}
        >
          {SCHOOL.map((item) => (
            <MenuItem key={item} value={item}>
              {item}
            </MenuItem>
          ))}
        </TextField>
      </Box>
    )
  }

  if (step === 'uso') {
    return (
      <Box sx={{ display: 'grid', gap: 2 }}>
        <OptionCards
          multiple
          value={form.purposes}
          onChange={(value) => patch({ purposes: value as string[] })}
          options={PURPOSES}
        />
        {form.purposes.includes('other') && (
          <TextField
            label="Qual?"
            value={form.purpose_other}
            onChange={(event) => patch({ purpose_other: event.target.value })}
          />
        )}
      </Box>
    )
  }

  if (step === 'renda') {
    const income = (
      <MoneyField
        label="Renda bruta mensal familiar"
        value={form.income}
        onChange={(value) => patch({ income: value })}
      />
    )
    if (!form.collegePrivate) {
      return <Box sx={{ display: 'grid', gap: 2 }}>{income}</Box>
    }
    return (
      <Box sx={{ display: 'grid', gap: 2 }}>
        <PillGroup
          multiple
          label="Bolsa de estudos"
          value={form.scholarships}
          onChange={(value) => patch({ scholarships: value as string[] })}
          options={SCHOLARSHIPS}
        />
        {form.scholarships.includes('other') && (
          <TextField
            label="Quais as outras bolsas?"
            value={form.scholarships_other}
            onChange={(event) => patch({ scholarships_other: event.target.value })}
          />
        )}
        {form.scholarships.length > 0 && (
          <Field label="Percentual da bolsa">
            <MaskedInput
              format="percentage"
              value={form.scholarship_percentage}
              onChange={(event) => patch({ scholarship_percentage: event.target.value })}
            />
          </Field>
        )}
        <PillGroup
          multiple
          label="Financiamento estudantil"
          value={form.financing}
          onChange={(value) => patch({ financing: value as string[] })}
          options={FINANCING}
        />
        {form.financing.includes('other') && (
          <TextField
            label="Quais os outros financiamentos?"
            value={form.financing_other}
            onChange={(event) => patch({ financing_other: event.target.value })}
          />
        )}
        {form.financing.length > 0 && (
          <Field label="Percentual do financiamento">
            <MaskedInput
              format="percentage"
              value={form.financing_percentage}
              onChange={(event) => patch({ financing_percentage: event.target.value })}
            />
          </Field>
        )}
        <PillGroup
          multiple
          label="Como as mensalidades são pagas?"
          value={form.payment_sources}
          onChange={(value) => patch({ payment_sources: value as string[] })}
          options={PAYMENT_SOURCES}
        />
        {income}
      </Box>
    )
  }

  if (step === 'msg') {
    return (
      <Field label="Caso queira nos deixar alguma mensagem ou nos contar um pouco mais sobre você, use o quadro abaixo (opcional):">
        <TextField
          multiline
          minRows={4}
          value={form.free_text}
          onChange={(event) => patch({ free_text: event.target.value })}
          slotProps={{ htmlInput: { 'aria-label': 'Mensagem (opcional)' } }}
        />
      </Field>
    )
  }

  if (step === 'traj') {
    return (
      <Box sx={{ display: 'grid', gap: 2 }}>
        <TextField
          select
          label="Onde estudou no ensino médio?"
          value={form.high_school_type}
          onChange={(event) => patch({ high_school_type: event.target.value })}
        >
          {HIGH_SCHOOL.map((item) => (
            <MenuItem key={item.value} value={item.value}>
              {item.label}
            </MenuItem>
          ))}
        </TextField>
        <PillGroup
          label="Já recebeu prêmio ou bolsa por mérito acadêmico?"
          value={form.academic_award}
          onChange={(value) => patch({ academic_award: String(value) })}
          options={YES_NO}
        />
        {form.academic_award === 'yes' && (
          <TextField
            label="Qual?"
            value={form.academic_award_description}
            onChange={(event) => patch({ academic_award_description: event.target.value })}
          />
        )}
        <PillGroup
          label="Já precisou trancar o curso?"
          value={form.course_locked}
          onChange={(value) => patch({ course_locked: String(value) })}
          options={YES_NO}
        />
        {form.course_locked === 'yes' && (
          <TextField
            multiline
            minRows={3}
            label="Qual o motivo?"
            value={form.course_locked_reason}
            onChange={(event) => patch({ course_locked_reason: event.target.value })}
          />
        )}
      </Box>
    )
  }

  if (step === 'planos') {
    return (
      <Box sx={{ display: 'grid', gap: 2 }}>
        <PillGroup
          multiple
          label="O que pretende fazer?"
          value={form.plans}
          onChange={(value) => patch({ plans: value as string[] })}
          options={PLANS}
        />
        <Field label="Quando pretende fazer residência?">
          <OptionCards
            value={form.residency_timing}
            onChange={(value) => patch({ residency_timing: String(value) })}
            options={RESIDENCY}
          />
        </Field>
      </Box>
    )
  }

  if (step === 'conta') {
    const bank = BANKS.find((item) => item.code === form.bank_code) ?? null
    return (
      <Box sx={{ display: 'grid', gap: 2 }}>
        <Autocomplete
          freeSolo
          options={BANKS}
          value={bank}
          getOptionLabel={(option) =>
            typeof option === 'string' ? option : `${option.code} · ${option.name}`
          }
          onChange={(_, value) => {
            if (typeof value === 'string') {
              patch({ bank_code: digits(value).slice(0, 3) })
              return
            }
            patch({ bank_code: value?.code ?? '' })
          }}
          renderInput={(params) => <TextField {...params} label="Banco" />}
        />
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
          <TextField
            label="Agência"
            value={form.agency_number}
            onChange={(event) => patch({ agency_number: event.target.value })}
          />
          <TextField
            label="Dígito (opcional)"
            value={form.agency_digit}
            onChange={(event) => patch({ agency_digit: event.target.value })}
          />
          <TextField
            label="Conta"
            value={form.account_number}
            onChange={(event) => patch({ account_number: event.target.value })}
          />
          <TextField
            label="Dígito"
            value={form.account_digit}
            onChange={(event) => patch({ account_digit: event.target.value })}
          />
        </Box>
        <PillGroup
          label="Tipo de conta"
          value={form.account_type}
          onChange={(value) => patch({ account_type: String(value) })}
          options={[
            { value: 'checking', label: 'Conta corrente' },
            { value: 'savings', label: 'Conta poupança' },
          ]}
        />
      </Box>
    )
  }

  if (step === 'docs') {
    return (
      <Box sx={{ display: 'grid', gap: 2 }}>
        <Box
          sx={{
            display: 'flex',
            gap: 1,
            p: 1.5,
            borderRadius: 1.5,
            bgcolor: 'action.hover',
          }}
        >
          <InfoOutlined color="primary" />
          <Typography variant="body2">
            <b>Atenção:</b> dê preferência a documentos com verificadores digitais de autenticidade.
          </Typography>
        </Box>
        {documentList(form.collegePrivate).map((type) => {
          const meta = DOCUMENTS[type]
          const sent = documents.filter((item) => item.type === type)
          return (
            <DocumentDropzone
              key={type}
              label={meta.label}
              description={meta.description}
              optional={meta.optional}
              uploading={uploadingType === type}
              approved={sent.length > 0 && sent.every((item) => item.status === 'approved')}
              files={sent
                .map((item) => ({
                  key: item.key,
                  name: item.original_name,
                  isPdf: item.original_name.toLowerCase().endsWith('.pdf'),
                }))}
              onDrop={(picked) =>
                picked.forEach((file) => onUpload(type, file))
              }
              onRemove={onRemove}
            />
          )
        })}
      </Box>
    )
  }

  if (step === 'analise') {
    return (
      <Box sx={{ textAlign: 'center', display: 'grid', gap: 1, py: 2 }}>
        {denied ? (
          <>
            <Cancel color="error" sx={{ fontSize: 48, mx: 'auto' }} />
            <Typography variant="h6">Seu pedido não foi aprovado</Typography>
            <Typography color="text.secondary">
              Infelizmente não poderemos te apoiar neste momento.
            </Typography>
          </>
        ) : (
          <>
            <HourglassTop color="primary" sx={{ fontSize: 48, mx: 'auto' }} />
            <Typography variant="h6">Em análise</Typography>
            <Typography color="text.secondary">
              Recebemos tudo. Você vai receber o resultado por e-mail em até 2 dias úteis.
            </Typography>
            <Typography>
              Mesada {reais(form.amount)}/mês · {form.combo ? 'Com contabilidade' : 'Sem contabilidade'}
            </Typography>
          </>
        )}
      </Box>
    )
  }

  if (step === 'proposta') {
    if (!proposal) {
      return <Typography>A proposta ainda não está disponível.</Typography>
    }
    const strong = (text: string) => (
      <Box component="b" sx={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
        {text}
      </Box>
    )
    const payment = proposal.cashflow[0]?.value ?? 0
    const validUntil = brazilDate(proposal.expiration_date)
    return (
      <Box sx={{ display: 'grid', gap: 1.5 }}>
        <Typography>
          Mesada de {strong(reais(Number(proposal.amount)))} / mês, durante{' '}
          {strong(`${proposal.disbursements} meses`)}.
        </Typography>
        <Typography>
          Você paga {strong(`${proposal.installments}x de ${reais(payment)}`)}
          {proposal.grace_period
            ? `, começando ${proposal.grace_period} ${proposal.grace_period === 1 ? 'mês' : 'meses'} depois da formatura`
            : ''}
          .
        </Typography>
        <Typography color="text.secondary">
          Taxa de {(Number(proposal.monthly_interest) * 100).toFixed(2).replace('.', ',')}% a.m.
        </Typography>
        {proposalExpired ? (
          <Alert
            severity="warning"
            action={
              <Button color="inherit" size="small" disabled={reissuing} onClick={onReissue}>
                Reemitir proposta expirada
              </Button>
            }
          >
            Esta proposta expirou em {validUntil}.
          </Alert>
        ) : (
          <Typography color="text.secondary">Proposta válida até {validUntil}.</Typography>
        )}
      </Box>
    )
  }

  if (step === 'assinar') {
    return <Typography>A assinatura do contrato entra numa próxima etapa.</Typography>
  }

  return (
    <Box sx={{ textAlign: 'center', py: 2 }}>
      <CheckCircle color="success" sx={{ fontSize: 48 }} />
      <Typography variant="h6">Pronto</Typography>
      <Typography color="text.secondary">Sua mesada foi contratada.</Typography>
    </Box>
  )
}
